/**
 * Export Project Modal
 *
 * Modal for exporting a project with optional artifact selection.
 * Uses the reusable ArtifactSelectionList component.
 *
 * Features:
 * - Export entire project (all artifacts)
 * - Export selected artifacts only
 * - Shows what will be exported
 * - Displays warning if only one artifact selected
 */

import { useState, useEffect } from 'react';
import { X, Download, AlertCircle, Package } from 'lucide-react';
import { Button } from '../ui/Button';
import { ArtifactSelectionList, type ArtifactGroup } from '../ui/ArtifactSelectionList';
import type { Project } from '@/stores/projectStore';
import type { Artifact } from '@/types/artifactDesign';
import { buildUserHeaders } from '@/stores/userStore';
import { API_BASE_URL } from '@/lib/config';

interface ExportProjectModalProps {
  project: Project;
  selectedArtifactId?: string | null;
  onClose: () => void;
}

export const ExportProjectModal: React.FC<ExportProjectModalProps> = ({
  project,
  selectedArtifactId = null,
  onClose
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [groups, setGroups] = useState<ArtifactGroup[]>([]);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  // Initialize groups from project artifacts
  useEffect(() => {
    // Filter to only include artifacts with completed lumaJson (ready for export)
    const artifacts = Object.values(project.artifacts).filter(
      (artifact) => artifact.lumaJson !== null
    );

    // Group artifacts by type
    const skillArtifacts: Artifact[] = [];
    const subskillArtifacts: Artifact[] = [];
    const subflowArtifacts: Artifact[] = [];
    const attributeArtifacts: Artifact[] = [];
    const integrationArtifacts: Artifact[] = [];
    const facetArtifacts: Artifact[] = [];

    artifacts.forEach((artifact) => {
      // Smart default selection:
      // - If selectedArtifactId is provided, only select that artifact
      // - Otherwise, select all artifacts
      const isSelected = selectedArtifactId
        ? artifact.id === selectedArtifactId
        : true;

      const artifactWithSelection = { ...artifact, selected: isSelected };

      switch (artifact.type) {
        case 'SKILL':
          skillArtifacts.push(artifactWithSelection);
          break;
        case 'SUBSKILL':
          subskillArtifacts.push(artifactWithSelection);
          break;
        case 'SUBFLOW':
          subflowArtifacts.push(artifactWithSelection);
          break;
        case 'ATTRIBUTE':
          attributeArtifacts.push(artifactWithSelection);
          break;
        case 'INTEGRATION':
          integrationArtifacts.push(artifactWithSelection);
          break;
        case 'FACET':
          facetArtifacts.push(artifactWithSelection);
          break;
      }
    });

    setGroups([
      { type: 'skill', artifacts: skillArtifacts },
      { type: 'subskill', artifacts: subskillArtifacts },
      { type: 'subflow', artifacts: subflowArtifacts },
      { type: 'attribute', artifacts: attributeArtifacts },
      { type: 'integration', artifacts: integrationArtifacts },
      { type: 'facet', artifacts: facetArtifacts }
    ]);

    // Auto-enable "Show Selected Only" filter if a single artifact is selected
    setShowSelectedOnly(!!selectedArtifactId);
  }, [project.artifacts, selectedArtifactId]);

  const handleSelectionChange = (
    artifactId: string,
    artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet',
    selected: boolean
  ) => {
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.type === artifactType) {
          return {
            ...group,
            artifacts: group.artifacts.map((artifact) =>
              artifact.id === artifactId ? { ...artifact, selected } : artifact
            )
          };
        }
        return group;
      })
    );
  };

  const handleTypeToggle = (
    artifactType: 'skill' | 'subskill' | 'subflow' | 'attribute' | 'integration' | 'facet',
    selected: boolean
  ) => {
    setGroups((prevGroups) =>
      prevGroups.map((group) => {
        if (group.type === artifactType) {
          return {
            ...group,
            artifacts: group.artifacts.map((artifact) => ({ ...artifact, selected }))
          };
        }
        return group;
      })
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setGroups((prevGroups) =>
      prevGroups.map((group) => ({
        ...group,
        artifacts: group.artifacts.map((artifact) => ({ ...artifact, selected }))
      }))
    );
  };

  const totalSelected = groups.reduce((sum, g) => sum + g.artifacts.filter(a => a.selected).length, 0);
  const totalArtifacts = groups.reduce((sum, g) => sum + g.artifacts.length, 0);
  const totalInProject = Object.values(project.artifacts).length;
  const filteredOutCount = totalInProject - totalArtifacts;
  const isExportingAll = totalSelected === totalArtifacts;
  const isOnlyOneSelected = totalSelected === 1;

  const handleExport = async () => {
    setIsExporting(true);

    try {
      // Build list of selected artifact IDs
      const selectedArtifactIds: string[] = [];
      groups.forEach((group) => {
        group.artifacts.forEach((artifact) => {
          if (artifact.selected) {
            selectedArtifactIds.push(artifact.id);
          }
        });
      });

      // Call export API
      const response = await fetch(`${API_BASE_URL}/projects/${project.id}/export`, {
        method: 'POST',
        headers: buildUserHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          artifactIds: isExportingAll ? undefined : selectedArtifactIds
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to export project');
      }

      // Get the ZIP blob
      const blob = await response.blob();

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${project.name}_${project.suffix}.zip`;
      if (contentDisposition) {
        // Match filename="..." or filename=... patterns
        const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"'\n;]+)["']?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      console.log('[ExportProjectModal] Project exported successfully:', filename);
      onClose();
    } catch (error) {
      console.error('[ExportProjectModal] Failed to export project:', error);
      alert(`Failed to export project: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border border-border rounded-lg shadow-lg max-w-4xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold">Export Project</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            disabled={isExporting}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 flex-shrink-0">
          {/* Project Info */}
          <div className="bg-muted rounded-md p-4">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-medium text-sm">
                  {project.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {isExportingAll
                    ? `Exporting all ${totalArtifacts} ${totalArtifacts === 1 ? 'artifact' : 'artifacts'}`
                    : `Exporting ${totalSelected} of ${totalArtifacts} ${totalArtifacts === 1 ? 'artifact' : 'artifacts'}`
                  }
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Suffix</p>
                <p className="text-sm font-mono">{project.suffix}</p>
              </div>
            </div>
          </div>

          {/* Info: Filtered artifacts (not ready for export) */}
          {filteredOutCount > 0 && (
            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900/50 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-900 dark:text-blue-100">
                <p className="font-medium">{filteredOutCount} {filteredOutCount === 1 ? 'artifact is' : 'artifacts are'} not ready for export</p>
                <p className="mt-1">
                  {filteredOutCount === 1 ? 'This artifact does' : 'These artifacts do'} not have LumaJSON data generated yet. Only artifacts that have completed their generation pipeline can be exported to Luma.
                </p>
              </div>
            </div>
          )}

          {/* Warning for single artifact */}
          {isOnlyOneSelected && (
            <div className="bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/50 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-orange-900 dark:text-orange-100">
                <p className="font-medium">Only one artifact selected</p>
                <p className="mt-1">
                  To export multiple artifacts, select them below. To export the entire project, click "Select All" or keep all artifacts selected.
                </p>
              </div>
            </div>
          )}

          {/* Help Text */}
          <div className="text-sm text-muted-foreground">
            <p>Select which artifacts to include in the export. Only artifacts with completed LumaJSON are shown.</p>
          </div>
        </div>

        {/* Artifact Selection List */}
        <div className="flex-1 overflow-auto border-t border-border">
          {totalArtifacts === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 px-4 text-center">
              <Package className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No artifacts ready for export</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                This project has {totalInProject} {totalInProject === 1 ? 'artifact' : 'artifacts'}, but {totalInProject === 1 ? 'it has' : 'they have'} not completed the LumaJSON generation stage yet.
                Complete the artifact pipeline to generate LumaJSON data before exporting.
              </p>
            </div>
          ) : (
            <ArtifactSelectionList
              groups={groups}
              onSelectionChange={handleSelectionChange}
              onTypeToggle={handleTypeToggle}
              onSelectAll={handleSelectAll}
              showSelectedOnly={showSelectedOnly}
              onToggleShowSelected={setShowSelectedOnly}
              mode="export"
            />
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0">
          <div className="text-sm text-muted-foreground">
            {totalSelected} {totalSelected === 1 ? 'artifact' : 'artifacts'} selected
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={totalSelected === 0 || isExporting}
            >
              {isExporting ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                  Exporting...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Export ({totalSelected})
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
